import { useMemo, useState, useCallback, useRef, useEffect, memo } from 'react'
// @mui
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'

import { OptionType } from 'src/components/hook-form/rhf-autocomplete'
import { useBoolean } from 'src/hooks/use-boolean'
import { LoadingButton } from '@mui/lab'
import useDebounce from 'src/hooks/use-debounce'
import useCategories from 'src/service/categories.service'
import { Autocomplete } from '@mui/material'
import { Controller, useFieldArray } from 'react-hook-form'
import Iconify from 'src/components/iconify'
import { RHFTextField } from 'src/components/hook-form'
import useProducts from 'src/service/products.service'
import { masks } from 'src/utils/masks'
import axiosInstance from 'src/utils/axios'
import { useSnackbar } from 'src/components/snackbar'
import { ConfirmDialog } from 'src/components/custom-dialog'
import UserCreateEditForm from '../../users/user-create-edit-view'

// ----------------------------------------------------------------------

interface EventStepperThreeProps {
  methods: any
  activeStep: number
  createId?: string
  handleBack: () => void
  handleNext: (data: any) => void
  isLoading?: boolean
  handleSave: () => Promise<void>
}

export default function EventStepperThree({
  activeStep,
  handleBack,
  handleNext,
  methods,
  isLoading,
  handleSave,
  createId,
}: EventStepperThreeProps) {
  const [search, setSearch] = useState('')
  const searchDebounce = useDebounce(search, 300)
  const { enqueueSnackbar } = useSnackbar()
  const lastActivityRef = useRef(Date.now())

  useEffect(() => {
    if (!createId) {
      enqueueSnackbar('Erro: Evento não foi criado. Volte e salve o evento primeiro.', {
        variant: 'error',
        autoHideDuration: 5000,
      })
    }
  }, [createId, enqueueSnackbar])

  const { control, watch, getValues, setValue } = methods

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'categories',
  })

  const watchedCategories = watch('categories') || []
  const totalProducts = watchedCategories.reduce(
    (acc: number, cat: any) => acc + (cat?.products?.length || 0),
    0,
  )

  const {
    isLoading: isLoadingCategories,
    isFetching: isFetchingCategories,
    data: categoriesData,
    refetch: refetchCategories,
  } = useCategories(
    {
      page: 1,
      perPage: 100,
      sort: undefined,
    },
    {
      search: searchDebounce,
    },
  )

  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now()
    }

    const events = ['mousedown', 'keypress', 'touchstart']
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true, capture: true })
    })

    const fiveMinutes = 5 * 60 * 1000
    const threeMinutes = 3 * 60 * 1000

    const refreshInterval = setInterval(async () => {
      if (Date.now() - lastActivityRef.current > threeMinutes) {
        try {
          await refetchCategories()
        } catch {
          // ignore
        }
      }
    }, fiveMinutes)

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true)
      })
      clearInterval(refreshInterval)
    }
  }, [refetchCategories])

  const categoryOptions = useMemo(() => {
    let categories: any = categoriesData?.data || []
    categories = categories.map((category: any) => ({
      label: category.name,
      value: category._id,
    }))

    const hasOption = categories.find((category: any) => category.label === search)
    if (!hasOption && search.trim()) {
      return [...categories, { label: search, value: search, isNew: true }]
    }

    return categories
  }, [search, categoriesData?.data])

  const addCategory = useCallback(
    async (newCategory: any) => {
      // Always create a new category instance for this event (whether existing or new)
      if (createId) {
        try {
          const isExistingCategory = !newCategory.isNew && newCategory.value

          enqueueSnackbar(
            isExistingCategory ? 'Criando categoria para este evento...' : 'Criando categoria...',
            {
              variant: 'info',
              autoHideDuration: 1000,
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
            },
          )

          const response = await axiosInstance.post('/category', {
            name: newCategory.label || newCategory.value.trim(),
            event: createId,
            generalTax: {
              enabled: false,
              value: 0,
            },
            transfer: {
              type: 'brut',
              value: 0,
            },
            controlStock: false,
            validateQrcode: false,
          })

          // Add the created category with real ID to the form
          const createdCategory = response.data
          append({
            name: createdCategory.name,
            _id: createdCategory._id,
            isNew: false, // Now it's saved in the database
            generalTax: createdCategory.generalTax || {
              enabled: false,
              value: 0,
            },
            transfer: createdCategory.transfer || {
              type: 'brut',
              value: 0,
            },
            controlStock: createdCategory.controlStock || false,
            products: [],
          })

          enqueueSnackbar(
            isExistingCategory
              ? 'Categoria criada para este evento!'
              : 'Categoria criada com sucesso!',
            {
              variant: 'success',
              autoHideDuration: 1500,
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
            },
          )
        } catch (error: any) {
          enqueueSnackbar('Erro ao criar categoria', {
            variant: 'error',
            autoHideDuration: 2000,
          })
        }
      } else {
        // If no event ID, add as temporary category
        append({
          name: newCategory.label || newCategory.value,
          _id: `temp_cat_${Date.now()}`, // Temporary ID
          isNew: true,
          generalTax: {
            enabled: false,
            value: 0,
          },
          transfer: {
            type: 'brut',
            value: 0,
          },
          controlStock: false,
          products: [],
        })
        enqueueSnackbar('Categoria adicionada (será salva ao salvar o evento)', {
          variant: 'info',
          autoHideDuration: 2000,
        })
      }

      setSearch('')
    },
    [append, createId, enqueueSnackbar],
  )

  const handleAddCategory = (newValue: any) => {
    if (!newValue) return
    if (typeof newValue === 'string') {
      const name = newValue.trim()
      if (!name) return
      addCategory({ label: name, value: name, isNew: true })
      return
    }
    addCategory(newValue)
  }

  return (
    <>
      <Stack gap={3}>
        {/* Event ID warning */}
        {!createId && (
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              ⚠️ Evento não foi criado ainda
            </Typography>
            <Typography variant='body2'>
              Para adicionar categorias e produtos, é necessário que o evento seja criado primeiro.
              Volte ao step anterior e clique em &quot;Próximo&quot; para criar o evento.
            </Typography>
          </Alert>
        )}

        {/* Quick Category Add */}
        <Paper
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            bgcolor: 'primary.lighter',
            border: '1px solid',
            borderColor: 'primary.light',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={{ xs: 1.5, sm: 2 }}
          >
            <Stack direction='row' alignItems='center' gap={1} flexShrink={0}>
              <Iconify
                icon='mdi:food-apple'
                sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }}
              />
              <Typography
                variant='subtitle2'
                color='primary.dark'
                sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' }, fontWeight: 600 }}
              >
                Cardápio
              </Typography>
              <Chip
                size='small'
                label={`${fields.length} ${fields.length === 1 ? 'categoria' : 'categorias'}`}
                color='primary'
                variant='soft'
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
              <Chip
                size='small'
                label={`${totalProducts} ${totalProducts === 1 ? 'produto' : 'produtos'}`}
                color='info'
                variant='soft'
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Stack>

            <Autocomplete
              freeSolo
              selectOnFocus
              clearOnBlur={false}
              options={categoryOptions}
              loading={isLoadingCategories || isFetchingCategories}
              getOptionLabel={(option: any) => option?.label || ''}
              onChange={(_, newValue: any) => handleAddCategory(newValue)}
              inputValue={search}
              onInputChange={(_, newInputValue) => setSearch(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder='Nova categoria (Enter para criar)'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault()
                  }}
                  variant='outlined'
                  size='small'
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'background.paper',
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={(option as any).value}>
                  <Stack direction='row' alignItems='center' gap={1} width='100%'>
                    <Iconify
                      icon={(option as any).isNew ? 'mdi:plus-circle' : 'mdi:content-copy'}
                      sx={{ color: (option as any).isNew ? 'success.main' : 'info.main' }}
                    />
                    <Stack flex={1}>
                      <Typography variant='body2'>{(option as any).label}</Typography>
                      {!(option as any).isNew && (
                        <Typography variant='caption' color='text.secondary'>
                          Criar para este evento
                        </Typography>
                      )}
                    </Stack>
                    <Chip
                      label={(option as any).isNew ? 'Nova' : 'Copiar'}
                      size='small'
                      color={(option as any).isNew ? 'success' : 'info'}
                      variant='soft'
                    />
                  </Stack>
                </li>
              )}
              sx={{ flex: 1, maxWidth: { xs: '100%', sm: 420 } }}
            />
          </Stack>
        </Paper>

        {/* Categories */}
        {fields.length > 0 ? (
          <Stack gap={2}>
            {fields.map((field: any, index: number) => (
              <CategorySection
                key={field.id}
                field={field}
                index={index}
                control={control}
                watch={watch}
                remove={remove}
                createId={createId}
                setValue={setValue}
              />
            ))}
          </Stack>
        ) : (
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Iconify
              icon='mdi:food-outline'
              sx={{ fontSize: { xs: 40, sm: 48 }, color: 'grey.400', mb: 2 }}
            />
            <Typography
              variant='h6'
              color='text.secondary'
              gutterBottom
              sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
            >
              Nenhuma categoria adicionada
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
            >
              Adicione categorias para organizar os produtos do seu evento
            </Typography>
          </Paper>
        )}
      </Stack>

      <Stack spacing={2} sx={{ mt: 3 }}>
        {/* Info sobre auto-save */}
        <Paper
          sx={{
            p: 2,
            bgcolor: 'success.lighter',
            border: '1px solid',
            borderColor: 'success.light',
          }}
        >
          <Stack direction='row' alignItems='center' gap={1}>
            <Iconify icon='solar:check-circle-bold' sx={{ color: 'success.main' }} />
            <Typography variant='body2' color='success.dark'>
              <strong>Salvo automaticamente:</strong> Todas as alterações no cardápio são salvas
              automaticamente. Clique em &quot;Finalizar&quot; quando terminar de configurar o
              evento.
            </Typography>
          </Stack>
        </Paper>

        <Stack
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          justifyContent='space-between'
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 0 }}
        >
          <Button
            disabled={activeStep === 0}
            variant='outlined'
            onClick={handleBack}
            startIcon={<Iconify icon='solar:arrow-left-bold' />}
            size='large'
            sx={{ order: { xs: 2, sm: 1 } }}
          >
            Voltar
          </Button>
          <LoadingButton
            loading={isLoading}
            variant='contained'
            color='success'
            type='submit'
            endIcon={<Iconify icon='solar:check-circle-bold' />}
            size='large'
            sx={{
              order: { xs: 1, sm: 2 },
              minWidth: 200,
              fontWeight: 600,
            }}
          >
            Finalizar Evento
          </LoadingButton>
        </Stack>
      </Stack>
    </>
  )
}

// ----------------------------------------------------------------------

interface CategorySectionProps {
  field: any
  index: number
  control: any
  watch: any
  remove: (index: number) => void
  createId?: string
  setValue: any
}

const CategorySection = memo(({
  field,
  index,
  control,
  watch,
  remove,
  createId,
  setValue,
}: CategorySectionProps) => {
  const [expanded, setExpanded] = useState(true)
  const [savingCategory, setSavingCategory] = useState(false)
  const [isDeletingCategory, setIsDeletingCategory] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [tempName, setTempName] = useState(field.name)
  const confirm = useBoolean()
  const { enqueueSnackbar } = useSnackbar()

  // Get current category name from watch (real-time updates)
  const currentCategoryName = watch(`categories.${index}.name`) || field.name

  // Sync tempName when category name changes
  useEffect(() => {
    setTempName(currentCategoryName)
  }, [currentCategoryName])

  // Handle category deletion with cascade delete
  const handleDeleteCategory = async () => {
    const categoryId = field._id
    const products = field.products || []

    // If it's a temporary category that wasn't saved yet, just remove from form
    if (!categoryId || categoryId.toString().startsWith('temp_cat_')) {
      remove(index)
      confirm.onFalse()
      enqueueSnackbar('Categoria removida', {
        variant: 'success',
        autoHideDuration: 1000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
      return
    }

    setIsDeletingCategory(true)
    try {
      // Delete category with cascade delete (backend will handle products)
      const response = await axiosInstance.delete(`/category/${categoryId}`)

      // Remove from form after successful API call
      remove(index)
      confirm.onFalse()

      const deletedCount = response.data?.deletedProducts || products.length
      enqueueSnackbar(`Categoria e ${deletedCount} produto(s) deletados com sucesso`, {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
    } catch (error: any) {
      enqueueSnackbar('Erro ao deletar categoria', {
        variant: 'error',
        autoHideDuration: 2000,
      })
    } finally {
      setIsDeletingCategory(false)
    }
  }

  // Auto-save category settings
  const autoSaveCategory = async (fieldName: string, value: any) => {
    const categoryId = field._id

    // Skip if temporary category
    if (!categoryId || categoryId.toString().startsWith('temp_cat_')) {
      return
    }

    setSavingCategory(true)
    try {
      await axiosInstance.put(`/category/${categoryId}`, {
        [fieldName]: value,
      })

      enqueueSnackbar('Categoria atualizada', {
        variant: 'success',
        autoHideDuration: 800,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
    } catch (error: any) {
      enqueueSnackbar('Erro ao atualizar categoria', {
        variant: 'error',
        autoHideDuration: 1500,
      })
    } finally {
      setSavingCategory(false)
    }
  }

  // Save category name
  const saveCategoryName = async () => {
    const categoryId = field._id
    const newName = tempName.trim()

    if (!newName) {
      enqueueSnackbar('Nome da categoria não pode estar vazio', {
        variant: 'error',
        autoHideDuration: 2000,
      })
      setTempName(currentCategoryName) // Reset to original name
      setEditingName(false)
      return
    }

    if (newName === currentCategoryName) {
      setEditingName(false)
      return
    }

    // Skip if temporary category - just update the form
    if (!categoryId || categoryId.toString().startsWith('temp_cat_')) {
      setValue(`categories.${index}.name`, newName)
      setEditingName(false)
      enqueueSnackbar('Nome atualizado (será salvo ao salvar o evento)', {
        variant: 'info',
        autoHideDuration: 2000,
      })
      return
    }

    setSavingCategory(true)
    try {
      await axiosInstance.put(`/category/${categoryId}`, {
        name: newName,
      })

      // Update the form value
      setValue(`categories.${index}.name`, newName)
      setEditingName(false)

      enqueueSnackbar('Nome da categoria salvo', {
        variant: 'success',
        autoHideDuration: 800,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
    } catch (error: any) {
      enqueueSnackbar('Erro ao salvar nome da categoria', {
        variant: 'error',
        autoHideDuration: 1500,
      })
      setTempName(currentCategoryName) // Reset to original name
    } finally {
      setSavingCategory(false)
    }
  }

  const handleNameClick = () => {
    setTempName(currentCategoryName)
    setEditingName(true)
  }

  const handleNameKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveCategoryName()
    } else if (event.key === 'Escape') {
      setTempName(currentCategoryName)
      setEditingName(false)
    }
  }

  return (
    <>
      <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary
          expandIcon={<Iconify icon='eva:arrow-ios-downward-fill' />}
          sx={{
            '&:hover .delete-button': { opacity: 1 },
            '.delete-button': { opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.2s' },
            px: { xs: 1, sm: 2 },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent='space-between'
            width='100%'
            spacing={{ xs: 1, sm: 0 }}
            pr={{ xs: 1, sm: 2 }}
          >
            <Stack direction='row' alignItems='center' gap={{ xs: 1, sm: 2 }}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Iconify
                  icon='mdi:food-outline'
                  sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }}
                />
                {editingName ? (
                  <TextField
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onBlur={saveCategoryName}
                    onKeyDown={handleNameKeyDown}
                    size='small'
                    variant='standard'
                    autoFocus
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '1.125rem' },
                        fontWeight: 600,
                        padding: '2px 4px',
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant='subtitle1'
                    fontWeight={600}
                    onClick={handleNameClick}
                    sx={{
                      cursor: 'pointer',
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        px: 0.5,
                      },
                      transition: 'all 0.2s',
                    }}
                    title='Clique para editar'
                  >
                    {currentCategoryName}
                  </Typography>
                )}
                {savingCategory && <CircularProgress size={16} sx={{ color: 'primary.main' }} />}
                {field.isNew && (
                  <Chip
                    label='Nova'
                    size='small'
                    color='primary'
                    variant='soft'
                    sx={{ height: { xs: 18, sm: 20 } }}
                  />
                )}
              </Stack>
            </Stack>

            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Chip
                label={`${field?.products?.length || 0} produtos`}
                size='small'
                variant='outlined'
                color='info'
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              />
              {/* Indicador QR Code */}
              {watch(`categories.${index}.validateQrcode.enabled`) && (
                <Chip
                  icon={<Iconify icon='mdi:qrcode' sx={{ fontSize: 14 }} />}
                  label='QR Code'
                  size='small'
                  color='success'
                  variant='soft'
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                />
              )}
              <IconButton
                className='delete-button'
                size='small'
                color='error'
                onClick={(e) => {
                  e.stopPropagation()
                  confirm.onTrue()
                }}
                disabled={isDeletingCategory}
                sx={{
                  ml: 1,
                  '&:hover:not(:disabled)': {
                    bgcolor: 'error.lighter',
                    transform: 'scale(1.1)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                  transition: 'all 0.2s',
                }}
              >
                {isDeletingCategory ? (
                  <CircularProgress size={16} color='error' />
                ) : (
                  <Iconify icon='solar:trash-bin-minimalistic-bold' />
                )}
              </IconButton>
            </Stack>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Stack gap={3}>
            {/* Category Settings - Simplified */}
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent='space-between'
                flexWrap='wrap'
                gap={2}
              >
                <Stack direction='row' alignItems='center' gap={1}>
                  <Iconify
                    icon='mdi:cog'
                    sx={{ color: 'text.secondary', fontSize: { xs: 16, sm: 18 } }}
                  />
                  <Typography
                    variant='subtitle2'
                    color='text.primary'
                    sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                  >
                    Configurações
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  gap={{ xs: 2, sm: 3 }}
                  flexWrap='wrap'
                  width={{ xs: '100%', sm: 'auto' }}
                >
                  {/* Controle de Estoque */}
                  <Stack direction='column' spacing={0.5}>
                    <Stack direction='row' alignItems='center' gap={1}>
                      <Iconify
                        icon='mdi:warehouse'
                        sx={{
                          color: 'warning.main',
                          fontSize: { xs: 16, sm: 18 },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Controller
                            name={`categories.${index}.controlStock`}
                            control={control}
                            render={({ field: fieldSwitch }) => (
                              <Switch
                                {...fieldSwitch}
                                checked={fieldSwitch.value}
                                onChange={(event) => {
                                  const newValue = event.target.checked
                                  fieldSwitch.onChange(newValue)
                                  autoSaveCategory('controlStock', newValue)
                                }}
                                color='warning'
                                size='small'
                              />
                            )}
                          />
                        }
                        label='Controlar Estoque'
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            fontWeight: 600,
                          },
                          m: 0,
                        }}
                      />
                    </Stack>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        ml: 4,
                        lineHeight: 1.2,
                      }}
                    >
                      📦 Monitora quantidade disponível e bloqueia vendas quando esgotar
                    </Typography>
                  </Stack>

                  {/* Geração de QR Code */}
                  <Stack direction='column' spacing={0.5}>
                    <Stack direction='row' alignItems='center' gap={1}>
                      <Iconify
                        icon='mdi:qrcode'
                        sx={{
                          color: 'success.main',
                          fontSize: { xs: 16, sm: 18 },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Controller
                            name={`categories.${index}.validateQrcode.enabled`}
                            control={control}
                            render={({ field: fieldSwitch }) => (
                              <Switch
                                {...fieldSwitch}
                                checked={fieldSwitch.value}
                                onChange={(event) => {
                                  const newValue = event.target.checked
                                  fieldSwitch.onChange(newValue)
                                  autoSaveCategory('validateQrcode', { enabled: newValue })
                                }}
                                color='success'
                                size='small'
                              />
                            )}
                          />
                        }
                        label='Gerar QR Code'
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            fontWeight: 600,
                          },
                          m: 0,
                        }}
                      />
                    </Stack>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        ml: 4,
                        lineHeight: 1.2,
                      }}
                    >
                      🎫 Produtos desta categoria gerarão QR Codes individuais para validação de
                      entrada
                    </Typography>
                  </Stack>

                  {/* Status */}
                  <Stack direction='row' alignItems='center' gap={1}>
                    <Iconify
                      icon={field.isNew ? 'mdi:new-box' : 'mdi:check-circle'}
                      sx={{
                        fontSize: { xs: 16, sm: 18 },
                        color: field.isNew ? 'warning.main' : 'success.main',
                      }}
                    />
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      fontWeight={500}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {field.isNew ? 'Nova' : 'Salva'}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            {/* Products Table */}
            <ProductsTable
              categoryIndex={index}
              control={control}
              createId={createId}
              setValue={setValue}
              watch={watch}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title='Deletar Categoria'
        content={
          <Stack gap={2}>
            <Typography variant='body2'>
              Tem certeza que deseja deletar a categoria <strong>{currentCategoryName}</strong>?
            </Typography>
            <Alert severity='warning' sx={{ bgcolor: 'warning.lighter' }}>
              <Typography variant='body2'>
                <strong>Atenção:</strong> Esta ação também deletará todos os{' '}
                <strong>{field?.products?.length || 0} produto(s)</strong> desta categoria.
              </Typography>
            </Alert>
            <Typography variant='caption' color='text.secondary'>
              Esta ação não pode ser desfeita.
            </Typography>
          </Stack>
        }
        action={
          <LoadingButton
            variant='contained'
            color='error'
            onClick={handleDeleteCategory}
            loading={isDeletingCategory}
            loadingIndicator='Deletando...'
          >
            Deletar Categoria
          </LoadingButton>
        }
      />
    </>
  )
})

// ----------------------------------------------------------------------

interface ProductsTableProps {
  categoryIndex: number
  control: any
  createId?: string
  setValue: any
  watch: any
}

const ProductsTable = ({
  categoryIndex,
  control,
  createId,
  setValue,
  watch,
}: ProductsTableProps) => {
  const [search, setSearch] = useState('')
  const [inputValue, setInputValue] = useState('')
  const searchDebounce = useDebounce(search, 300)
  const { enqueueSnackbar } = useSnackbar()

  const categoryId = watch(`categories.${categoryIndex}._id`)

  const watchedCategories: any[] = watch('categories') || []
  const categoriesKey = JSON.stringify(watchedCategories.map((c: any) => ({ _id: c._id, name: c.name })))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allCategories = useMemo(() => watchedCategories, [categoriesKey])

  const {
    isLoading: isLoadingProducts,
    isFetching: isFetchingProducts,
    data: productsData,
  } = useProducts(
    {
      page: 1,
      perPage: 50,
      sort: undefined,
    },
    {
      search: searchDebounce,
    },
  )

  const productOptions = useMemo(() => {
    const products: any = productsData?.data || []
    const hasOption = products.find((product: any) => product.name === search)

    if (!hasOption && search.trim()) {
      return [...products, { name: search, _id: search, isNew: true }]
    }

    return products
  }, [search, productsData?.data])

  const { fields, append, remove } = useFieldArray({
    control,
    name: `categories.${categoryIndex}.products`,
  })

  const handleMoveProduct = useCallback(
    async (productIndex: number, targetCategoryIndex: number) => {
      const product = watch(`categories.${categoryIndex}.products.${productIndex}`)
      const targetCategoryId = allCategories[targetCategoryIndex]?._id

      if (!product || !targetCategoryId || targetCategoryId.toString().startsWith('temp_cat_')) {
        enqueueSnackbar('Categoria destino inválida', { variant: 'error', autoHideDuration: 1500 })
        return
      }

      try {
        if (product._id && !product._id.toString().startsWith('temp_')) {
          await axiosInstance.put(`/product/${product._id}`, { category: targetCategoryId })
        }

        const targetProducts = watch(`categories.${targetCategoryIndex}.products`) || []
        setValue(`categories.${targetCategoryIndex}.products`, [...targetProducts, product])
        remove(productIndex)

        enqueueSnackbar(`Produto movido para ${allCategories[targetCategoryIndex]?.name}`, {
          variant: 'success',
          autoHideDuration: 1500,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        })
      } catch (error: any) {
        enqueueSnackbar('Erro ao mover produto', { variant: 'error', autoHideDuration: 2000 })
      }
    },
    [categoryIndex, allCategories, watch, setValue, remove, enqueueSnackbar],
  )

  const addProduct = useCallback(
    async (newProduct: any) => {
      if (!newProduct?.name) return

      // If it's an existing product from the database, we need to create a new instance for this event/category
      if (!newProduct.isNew && newProduct._id && createId) {
        try {
          enqueueSnackbar('Adicionando produto ao evento...', {
            variant: 'info',
            autoHideDuration: 1000,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          })

          // Get category ID - make sure we have a real category to associate with
          if (!categoryId || categoryId.toString().startsWith('temp_cat_')) {
            enqueueSnackbar('Erro: Categoria precisa ser salva primeiro', {
              variant: 'error',
              autoHideDuration: 2000,
            })
            return
          }

          const productPayload = {
            name: newProduct.name,
            price: newProduct.price || 0,
            cost: newProduct.cost || 0,
            stock: 0, // Estoque sempre zera ao copiar de outro evento
            event: createId,
            category: categoryId,
            active: true, // Always active when creating
          }

          const response = await axiosInstance.post('/product', productPayload)

          // Add the created product instance with real ID to the form
          const createdProduct = response.data
          const productData = {
            name: createdProduct.name,
            _id: createdProduct._id,
            isNew: false,
            price: masks.currency(createdProduct.price || 0),
            cost: masks.currency(createdProduct.cost || 0),
            newPrice: masks.currency(createdProduct.price || 0),
            stock: createdProduct.stock || 0,
            active: createdProduct.active,
            validateQrcode: createdProduct.validateQrcode || { enabled: false },
          }

          append(productData)

          enqueueSnackbar('Produto adicionado ao evento com sucesso!', {
            variant: 'success',
            autoHideDuration: 1500,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          })
        } catch (error: any) {
          enqueueSnackbar('Erro ao adicionar produto ao evento', {
            variant: 'error',
            autoHideDuration: 2000,
          })
        }

        setSearch('')
        setInputValue('')
        return
      }

      // If it's an existing product but no createId, add as temporary
      if (!newProduct.isNew && newProduct._id && !createId) {
        const productData = {
          name: newProduct.name,
          _id: `temp_existing_${newProduct._id}_${Date.now()}`, // Temporary ID for existing product
          isNew: true,
          originalId: newProduct._id, // Store original ID for later use
          price: newProduct.price ? masks.currency(newProduct.price) : '0,00',
          cost: newProduct.cost ? masks.currency(newProduct.cost) : '0,00',
          newPrice: newProduct.price ? masks.currency(newProduct.price) : '0,00',
          stock: 0, // Estoque sempre zera ao copiar de outro evento
          active: true, // Always active when creating
          validateQrcode: { enabled: false }, // Default false to avoid paper waste
        }

        append(productData)
        enqueueSnackbar('Produto adicionado (será associado ao evento quando salvar)', {
          variant: 'info',
          autoHideDuration: 2000,
        })

        setSearch('')
        setInputValue('')
        return
      }

      // For new products, create directly in the API
      if (createId) {
        try {
          enqueueSnackbar('Criando produto...', {
            variant: 'info',
            autoHideDuration: 1000,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          })

          if (!categoryId || categoryId.toString().startsWith('temp_cat_')) {
            enqueueSnackbar('Erro: Categoria precisa ser salva primeiro', {
              variant: 'error',
              autoHideDuration: 2000,
            })
            return
          }

          const productPayload = {
            name: newProduct.name.trim(),
            price: 0,
            cost: 0,
            stock: 0,
            event: createId,
            category: categoryId,
            active: true,
          }

          const response = await axiosInstance.post('/product', productPayload)

          // Add the created product with real ID to the form
          const createdProduct = response.data
          const productData = {
            name: createdProduct.name,
            _id: createdProduct._id,
            isNew: false, // Now it's saved in the database
            price: masks.currency(createdProduct.price || 0),
            cost: masks.currency(createdProduct.cost || 0),
            newPrice: masks.currency(createdProduct.price || 0),
            stock: createdProduct.stock || 0,
            active: createdProduct.active,
            validateQrcode: createdProduct.validateQrcode || { enabled: false },
          }

          append(productData)

          enqueueSnackbar('Produto criado com sucesso!', {
            variant: 'success',
            autoHideDuration: 1500,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          })
        } catch (error: any) {
          enqueueSnackbar('Erro ao criar produto', {
            variant: 'error',
            autoHideDuration: 2000,
          })
        }
      } else {
        // If no event ID, add as temporary product
        const productData = {
          name: newProduct.name,
          _id: `temp_${Date.now()}`, // Temporary ID
          isNew: true,
          price: '0,00',
          cost: '0,00',
          newPrice: '0,00',
          stock: 0,
          active: true, // Always active when creating
          validateQrcode: { enabled: false }, // Default false to avoid paper waste
        }

        append(productData)
        enqueueSnackbar('Produto adicionado (será salvo ao salvar o evento)', {
          variant: 'info',
          autoHideDuration: 2000,
        })
      }

      setSearch('')
      setInputValue('')
    },
    [append, createId, enqueueSnackbar, categoryId],
  )

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && search.trim()) {
      event.preventDefault()
      addProduct({ name: search, isNew: true })
    }
  }

  return (
    <Stack gap={2}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='subtitle2' sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
          Produtos
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
        >
          {fields.length} produtos
        </Typography>
      </Stack>

      {/* Quick Product Add */}
      <Autocomplete
        freeSolo
        selectOnFocus
        clearOnBlur={false}
        size='small'
        options={productOptions}
        loading={isLoadingProducts || isFetchingProducts}
        getOptionLabel={(option: any) => option?.name || ''}
        onChange={(_, newValue: any) => newValue && addProduct(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue)
          setSearch(newInputValue)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Adicionar produto'
            placeholder='Digite o nome e pressione Enter'
            onKeyDown={handleKeyDown}
            helperText='Digite o nome do produto e pressione Enter'
            size='small'
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option._id || option.name}>
            <Stack direction='row' alignItems='center' gap={1} width='100%'>
              {option.isNew && <Chip label='Novo' size='small' color='secondary' />}
              <Stack>
                <Typography variant='body2'>{option.name}</Typography>
                {!option.isNew && option.price && (
                  <Typography variant='caption' color='text.secondary'>
                    R$ {masks.currency(option.price)}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </li>
        )}
        sx={{ maxWidth: { xs: '100%', sm: 400 } }}
      />

      {/* Products Table - Responsive */}
      {fields.length > 0 && (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{ borderRadius: 2, overflow: 'hidden' }}
          >
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell width='30%' sx={{ fontWeight: 600, color: 'text.primary' }}>
                    <Stack direction='row' alignItems='center' gap={1}>
                      <Iconify icon='mdi:package-variant' sx={{ fontSize: 16 }} />
                      Produto
                    </Stack>
                  </TableCell>
                  <TableCell
                    align='right'
                    width='15%'
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
                      <Iconify icon='mdi:tag-outline' sx={{ fontSize: 16 }} />
                      Preço Original
                    </Stack>
                  </TableCell>
                  <TableCell
                    align='right'
                    width='15%'
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
                      <Iconify icon='mdi:currency-usd' sx={{ fontSize: 16 }} />
                      Novo Preço
                    </Stack>
                  </TableCell>
                  <TableCell
                    align='right'
                    width='15%'
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
                      <Iconify icon='mdi:calculator' sx={{ fontSize: 16 }} />
                      Custo
                    </Stack>
                  </TableCell>
                  <TableCell
                    align='right'
                    width='10%'
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
                      <Iconify icon='mdi:warehouse' sx={{ fontSize: 16 }} />
                      Estoque
                    </Stack>
                  </TableCell>
                  <TableCell
                    align='center'
                    width='8%'
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    <Stack direction='row' alignItems='center' justifyContent='center' gap={1}>
                      <Iconify icon='mdi:qrcode' sx={{ fontSize: 16 }} />
                      QR Code
                    </Stack>
                  </TableCell>
                  <TableCell
                    align='center'
                    width='8%'
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    <Stack direction='row' alignItems='center' justifyContent='center' gap={1}>
                      <Iconify icon='mdi:toggle-switch' sx={{ fontSize: 16 }} />
                      Status
                    </Stack>
                  </TableCell>
                  <TableCell
                    align='center'
                    width='4%'
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    {/* Ações */}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((product: any, productIndex: number) => (
                  <SmartProductRow
                    key={product.id}
                    product={product}
                    categoryIndex={categoryIndex}
                    productIndex={productIndex}
                    control={control}
                    onRemove={() => remove(productIndex)}
                    createId={createId}
                    setValue={setValue}
                    allCategories={allCategories}
                    onMoveToCategory={(targetIdx: number) => handleMoveProduct(productIndex, targetIdx)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Mobile Product Cards */}
      {fields.length > 0 && (
        <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
          {fields.map((product: any, productIndex: number) => (
            <MobileProductCard
              key={product.id}
              product={product}
              categoryIndex={categoryIndex}
              productIndex={productIndex}
              control={control}
              onRemove={() => remove(productIndex)}
              createId={createId}
              setValue={setValue}
              allCategories={allCategories}
              onMoveToCategory={(targetIdx: number) => handleMoveProduct(productIndex, targetIdx)}
            />
          ))}
        </Stack>
      )}

      {fields.length === 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
          >
            Nenhum produto adicionado nesta categoria
          </Typography>
        </Paper>
      )}
    </Stack>
  )
}

// ----------------------------------------------------------------------

interface SmartProductRowProps {
  product: any
  categoryIndex: number
  productIndex: number
  control: any
  onRemove: () => void
  createId?: string
  setValue: any
  allCategories?: any[]
  onMoveToCategory?: (targetCategoryIndex: number) => void
}

const SmartProductRow = memo(({
  product,
  categoryIndex,
  productIndex,
  control,
  onRemove,
  createId,
  setValue,
  allCategories = [],
  onMoveToCategory,
}: SmartProductRowProps) => {
  const [editingCell, setEditingCell] = useState<string | null>(null)
  const [savingFields, setSavingFields] = useState<{ [key: string]: boolean }>({})
  const [lastSavedValues, setLastSavedValues] = useState<{ [key: string]: any }>({})
  const [isDeleting, setIsDeleting] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})
  const saveTimeoutRef = useRef<{ [key: string]: NodeJS.Timeout }>({})
  const confirm = useBoolean()
  const { enqueueSnackbar } = useSnackbar()

  // Auto-save function with debounce
  const autoSave = useCallback(
    async (field: string, value: any, immediate = false) => {
      // Check if we have a valid product ID
      if (!product._id || product._id === 'search' || product._id.toString().startsWith('temp_')) {
        return
      }

      // Clear existing timeout for this field if not immediate
      if (!immediate && saveTimeoutRef.current[field]) {
        clearTimeout(saveTimeoutRef.current[field])
      }

      const performSave = async () => {
        // Skip if value hasn't changed
        if (lastSavedValues[field] === value) {
          return
        }

        setSavingFields((prev) => ({ ...prev, [field]: true }))
        try {
          let processedValue = value

          // Handle different field types
          if (field === 'newPrice' || field === 'cost') {
            const unmasked = masks.unmask(value.toString())
            processedValue = parseFloat(unmasked) || 0
          } else if (field === 'stock') {
            processedValue = parseInt(value, 10) || 0
          } else if (field === 'active') {
            processedValue = Boolean(value)
          } else if (field === 'validateQrcode') {
            processedValue = value // Já vem como { enabled: boolean }
          } else if (field === 'name') {
            processedValue = String(value).trim()
          }

          // Map frontend field names to backend field names
          const fieldMapping: { [key: string]: string } = {
            newPrice: 'price',
            cost: 'cost',
            stock: 'stock',
            name: 'name',
            active: 'active',
            validateQrcode: 'validateQrcode',
          }

          const backendField = fieldMapping[field] || field

          await axiosInstance.put(`/product/${product._id}`, {
            [backendField]: processedValue,
          })

          // Update last saved values
          setLastSavedValues((prev) => ({
            ...prev,
            [field]: value,
          }))

          // Show subtle success feedback
          const fieldLabels: { [key: string]: string } = {
            name: 'Nome',
            newPrice: 'Preço',
            cost: 'Custo',
            stock: 'Estoque',
            active: 'Status',
            validateQrcode: 'QR Code',
          }

          enqueueSnackbar(`${fieldLabels[field] || 'Campo'} salvo`, {
            variant: 'success',
            autoHideDuration: 800,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          })
        } catch (error: any) {
          enqueueSnackbar(`Erro ao salvar ${field}`, {
            variant: 'error',
            autoHideDuration: 1500,
          })
        } finally {
          setSavingFields((prev) => ({ ...prev, [field]: false }))
        }
      }

      // Execute immediately or with debounce
      if (immediate) {
        await performSave()
      } else {
        // Set timeout for debounced save
        saveTimeoutRef.current[field] = setTimeout(performSave, 1000)
      }
    },
    [product._id, lastSavedValues, enqueueSnackbar],
  )

  const handleCellClick = (field: string) => {
    setEditingCell(field)
    setTimeout(() => {
      const input = inputRefs.current[field]
      if (input) {
        input.focus()
        input.select()
      }
    }, 0)
  }

  const handleKeyDown = (event: React.KeyboardEvent, field: string) => {
    const fields = ['name', 'newPrice', 'cost', 'stock']
    const currentIndex = fields.indexOf(field)

    const findCellInCategory = (catIdx: number, rowIdx: number) =>
      document.querySelector(
        `[data-field="${field}"][data-category="${catIdx}"][data-row="${rowIdx}"]`,
      ) as HTMLElement | null

    const focusTarget = (target: HTMLElement, crossedCategory: boolean) => {
      target.click()
      // Smooth scroll: center when crossing categories, otherwise just keep visible
      target.scrollIntoView({
        behavior: 'smooth',
        block: crossedCategory ? 'center' : 'nearest',
        inline: 'nearest',
      })
    }

    const moveDown = (): boolean => {
      const sameCat = findCellInCategory(categoryIndex, productIndex + 1)
      if (sameCat) {
        focusTarget(sameCat, false)
        return true
      }
      for (let nextCat = categoryIndex + 1; nextCat < (allCategories?.length || 0); nextCat += 1) {
        const target = findCellInCategory(nextCat, 0)
        if (target) {
          focusTarget(target, true)
          return true
        }
      }
      return false
    }

    const moveUp = (): boolean => {
      if (productIndex > 0) {
        const sameCat = findCellInCategory(categoryIndex, productIndex - 1)
        if (sameCat) {
          focusTarget(sameCat, false)
          return true
        }
      }
      for (let prevCat = categoryIndex - 1; prevCat >= 0; prevCat -= 1) {
        const prevCategoryProducts = document.querySelectorAll(
          `[data-field="${field}"][data-category="${prevCat}"]`,
        )
        if (prevCategoryProducts.length > 0) {
          const target = prevCategoryProducts[prevCategoryProducts.length - 1] as HTMLElement
          focusTarget(target, true)
          return true
        }
      }
      return false
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      if (moveDown()) {
        setEditingCell(null)
      }
    } else if (event.key === 'Tab') {
      event.preventDefault()
      setEditingCell(null)
      const nextField = fields[currentIndex + 1]
      if (nextField) {
        setTimeout(() => handleCellClick(nextField), 0)
      }
    } else if (event.key === 'Escape') {
      setEditingCell(null)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (moveDown()) {
        setEditingCell(null)
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (moveUp()) {
        setEditingCell(null)
      }
    }
  }

  const handleBlur = (field: string, value: any) => {
    setEditingCell(null)
    // Immediate save on blur
    autoSave(field, value, true)
  }

  // Handle real-time changes with debounce
  const handleChange = (field: string, value: any) => {
    // Debounced save during typing
    autoSave(field, value, false)
  }

  const handleConfirmDelete = async () => {
    if (isDeleting) return

    if (!product._id || product._id === 'search' || product._id.toString().startsWith('temp_')) {
      onRemove()
      confirm.onFalse()
      enqueueSnackbar('Produto removido', {
        variant: 'success',
        autoHideDuration: 1000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
      return
    }

    setIsDeleting(true)
    try {
      await axiosInstance.delete(`/product/${product._id}`)
      onRemove()
      confirm.onFalse()
      enqueueSnackbar('Produto deletado com sucesso', {
        variant: 'success',
        autoHideDuration: 1500,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
    } catch (error: any) {
      enqueueSnackbar('Erro ao deletar produto', {
        variant: 'error',
        autoHideDuration: 2000,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      Object.values(saveTimeoutRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout)
      })
    },
    [],
  )

  return (
    <TableRow
      hover={!isDeleting}
      sx={{
        opacity: isDeleting ? 0.5 : 1,
        transition: 'all 0.3s ease-in-out',
        pointerEvents: isDeleting ? 'none' : 'auto',
        filter: isDeleting ? 'grayscale(0.5)' : 'none',
        '&:hover:not([data-deleting="true"])': {
          bgcolor: 'action.hover',
          '& .delete-button': { opacity: 1 },
        },
        '& .delete-button': { opacity: 0, transition: 'opacity 0.2s' },
        '&:last-child td': { borderBottom: 0 },
      }}
      data-deleting={isDeleting}
    >
      {/* Product Name */}
      <TableCell
        onClick={() => handleCellClick('name')}
        sx={{ cursor: 'pointer' }}
        data-field='name'
        data-row={productIndex}
        data-category={categoryIndex}
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Controller
            name={`categories.${categoryIndex}.products.${productIndex}.name`}
            control={control}
            render={({ field }) =>
              editingCell === 'name' ? (
                <TextField
                  {...field}
                  inputRef={(el) => {
                    inputRefs.current.name = el
                  }}
                  size='small'
                  variant='standard'
                  fullWidth
                  onKeyDown={(e) => handleKeyDown(e, 'name')}
                  onBlur={(e) => handleBlur('name', e.target.value)}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    handleChange('name', e.target.value)
                  }}
                />
              ) : (
                <Typography variant='body2' noWrap sx={{ flex: 1 }}>
                  {field.value}
                </Typography>
              )
            }
          />
          {savingFields.name && <CircularProgress size={12} sx={{ color: 'primary.main' }} />}
        </Stack>
      </TableCell>

      {/* Original Price */}
      <TableCell align='right'>
        <Controller
          name={`categories.${categoryIndex}.products.${productIndex}.price`}
          control={control}
          render={({ field }) => (
            <Typography variant='body2' color='text.secondary'>
              R$ {field.value || '0,00'}
            </Typography>
          )}
        />
      </TableCell>

      {/* New Price */}
      <TableCell
        align='right'
        onClick={() => handleCellClick('newPrice')}
        sx={{ cursor: 'pointer' }}
        data-field='newPrice'
        data-row={productIndex}
        data-category={categoryIndex}
      >
        <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
          <Controller
            name={`categories.${categoryIndex}.products.${productIndex}.newPrice`}
            control={control}
            render={({ field }) =>
              editingCell === 'newPrice' ? (
                <TextField
                  {...field}
                  inputRef={(el) => {
                    inputRefs.current.newPrice = el
                  }}
                  size='small'
                  variant='standard'
                  onKeyDown={(e) => handleKeyDown(e, 'newPrice')}
                  onBlur={(e) => handleBlur('newPrice', e.target.value)}
                  onChange={(e) => {
                    const masked = masks.currency(e.target.value)
                    field.onChange(masked)
                    handleChange('newPrice', masked)
                  }}
                  sx={{ textAlign: 'right' }}
                />
              ) : (
                <Typography variant='body2'>R$ {field.value || '0,00'}</Typography>
              )
            }
          />
          {savingFields.newPrice && <CircularProgress size={12} sx={{ color: 'primary.main' }} />}
        </Stack>
      </TableCell>

      {/* Cost */}
      <TableCell
        align='right'
        onClick={() => handleCellClick('cost')}
        sx={{ cursor: 'pointer' }}
        data-field='cost'
        data-row={productIndex}
        data-category={categoryIndex}
      >
        <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
          <Controller
            name={`categories.${categoryIndex}.products.${productIndex}.cost`}
            control={control}
            render={({ field }) =>
              editingCell === 'cost' ? (
                <TextField
                  {...field}
                  inputRef={(el) => {
                    inputRefs.current.cost = el
                  }}
                  size='small'
                  variant='standard'
                  onKeyDown={(e) => handleKeyDown(e, 'cost')}
                  onBlur={(e) => handleBlur('cost', e.target.value)}
                  onChange={(e) => {
                    const masked = masks.currency(e.target.value)
                    field.onChange(masked)
                    handleChange('cost', masked)
                  }}
                  sx={{ textAlign: 'right' }}
                />
              ) : (
                <Typography variant='body2'>R$ {field.value || '0,00'}</Typography>
              )
            }
          />
          {savingFields.cost && <CircularProgress size={12} sx={{ color: 'primary.main' }} />}
        </Stack>
      </TableCell>

      {/* Stock */}
      <TableCell
        align='right'
        onClick={() => handleCellClick('stock')}
        sx={{ cursor: 'pointer' }}
        data-field='stock'
        data-row={productIndex}
        data-category={categoryIndex}
      >
        <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
          <Controller
            name={`categories.${categoryIndex}.products.${productIndex}.stock`}
            control={control}
            render={({ field }) =>
              editingCell === 'stock' ? (
                <TextField
                  {...field}
                  inputRef={(el) => {
                    inputRefs.current.stock = el
                  }}
                  size='small'
                  variant='standard'
                  type='number'
                  onKeyDown={(e) => handleKeyDown(e, 'stock')}
                  onBlur={(e) => handleBlur('stock', parseInt(e.target.value, 10) || 0)}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    handleChange('stock', parseInt(e.target.value, 10) || 0)
                  }}
                  sx={{ textAlign: 'right' }}
                />
              ) : (
                <Typography variant='body2'>{field.value || 0}</Typography>
              )
            }
          />
          {savingFields.stock && <CircularProgress size={12} sx={{ color: 'primary.main' }} />}
        </Stack>
      </TableCell>

      {/* QR Code Switch */}
      <TableCell align='center'>
        <Stack direction='row' alignItems='center' justifyContent='center' gap={1}>
          <Controller
            name={`categories.${categoryIndex}.products.${productIndex}.validateQrcode`}
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                checked={
                  field.value?.enabled !== undefined ? field.value.enabled : Boolean(field.value)
                }
                onChange={(event) => {
                  const newValue = { enabled: event.target.checked }
                  field.onChange(newValue)
                  autoSave('validateQrcode', newValue, true)
                }}
                size='small'
                color='success'
              />
            )}
          />
          {savingFields.validateQrcode && (
            <CircularProgress size={12} sx={{ color: 'success.main' }} />
          )}
        </Stack>
      </TableCell>

      {/* Active Switch */}
      <TableCell align='center'>
        <Stack direction='row' alignItems='center' justifyContent='center' gap={1}>
          <Controller
            name={`categories.${categoryIndex}.products.${productIndex}.active`}
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                checked={field.value}
                onChange={(event) => {
                  field.onChange(event.target.checked)
                  autoSave('active', event.target.checked, true)
                }}
                size='small'
              />
            )}
          />
          {savingFields.active && <CircularProgress size={12} sx={{ color: 'primary.main' }} />}
        </Stack>
      </TableCell>

      {/* Actions */}
      <TableCell align='center'>
        <IconButton
          size='small'
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={isDeleting}
          sx={{ transition: 'all 0.15s ease-in-out' }}
        >
          {isDeleting ? (
            <CircularProgress size={16} />
          ) : (
            <Iconify icon='solar:menu-dots-bold' />
          )}
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 180, boxShadow: 3 } } }}
        >
          {allCategories.length > 1 && (
            <MenuItem
              disabled
              sx={{ typography: 'caption', color: 'text.disabled', py: 0.5, minHeight: 'auto' }}
            >
              Mover para...
            </MenuItem>
          )}
          {allCategories
            .map((cat: any, idx: number) => ({ ...cat, _catIdx: idx }))
            .filter((_: any, idx: number) => idx !== categoryIndex)
            .map((cat: any) => (
              <MenuItem
                key={cat._id}
                onClick={() => {
                  setMenuAnchor(null)
                  onMoveToCategory?.(cat._catIdx)
                }}
                sx={{ fontSize: '0.85rem' }}
              >
                <ListItemIcon>
                  <Iconify icon='solar:folder-send-bold-duotone' width={18} />
                </ListItemIcon>
                <ListItemText primary={cat.name} primaryTypographyProps={{ variant: 'body2' }} />
              </MenuItem>
            ))}
          {allCategories.length > 1 && <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 0.5 }} />}
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              confirm.onTrue()
            }}
            sx={{ color: 'error.main', fontSize: '0.85rem' }}
          >
            <ListItemIcon>
              <Iconify icon='solar:trash-bin-minimalistic-bold' width={18} sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText primary='Deletar' primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        </Menu>
      </TableCell>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title='Deletar Produto'
        content={
          <Typography variant='body2'>
            Tem certeza que deseja deletar o produto <strong>{product.name}</strong>? Esta ação não
            pode ser desfeita.
          </Typography>
        }
        action={
          <LoadingButton
            variant='contained'
            color='error'
            onClick={handleConfirmDelete}
            loading={isDeleting}
            loadingIndicator='Deletando...'
          >
            Deletar
          </LoadingButton>
        }
      />
    </TableRow>
  )
})

// ----------------------------------------------------------------------

interface MobileProductCardProps {
  product: any
  categoryIndex: number
  productIndex: number
  control: any
  onRemove: () => void
  createId?: string
  setValue: any
  allCategories?: any[]
  onMoveToCategory?: (targetCategoryIndex: number) => void
}

const MobileProductCard = memo(({
  product,
  categoryIndex,
  productIndex,
  control,
  onRemove,
  createId,
  setValue,
  allCategories = [],
  onMoveToCategory,
}: MobileProductCardProps) => {
  const [editingField, setEditingField] = useState<string | null>(null)
  const [savingFields, setSavingFields] = useState<{ [key: string]: boolean }>({})
  const [lastSavedValues, setLastSavedValues] = useState<{ [key: string]: any }>({})
  const [isDeleting, setIsDeleting] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})
  const saveTimeoutRef = useRef<{ [key: string]: NodeJS.Timeout }>({})
  const confirm = useBoolean()
  const { enqueueSnackbar } = useSnackbar()

  // Auto-save function with debounce
  const autoSave = useCallback(
    async (field: string, value: any, immediate = false) => {
      // Check if we have a valid product ID
      if (!product._id || product._id === 'search' || product._id.toString().startsWith('temp_')) {
        return
      }

      // Clear existing timeout for this field if not immediate
      if (!immediate && saveTimeoutRef.current[field]) {
        clearTimeout(saveTimeoutRef.current[field])
      }

      const performSave = async () => {
        // Skip if value hasn't changed
        if (lastSavedValues[field] === value) {
          return
        }

        setSavingFields((prev) => ({ ...prev, [field]: true }))
        try {
          let processedValue = value

          // Handle different field types
          if (field === 'newPrice' || field === 'cost') {
            const unmasked = masks.unmask(value.toString())
            processedValue = parseFloat(unmasked) || 0
          } else if (field === 'stock') {
            processedValue = parseInt(value, 10) || 0
          } else if (field === 'active') {
            processedValue = Boolean(value)
          } else if (field === 'validateQrcode') {
            processedValue = value // Já vem como { enabled: boolean }
          } else if (field === 'name') {
            processedValue = String(value).trim()
          }

          // Map frontend field names to backend field names
          const fieldMapping: { [key: string]: string } = {
            newPrice: 'price',
            cost: 'cost',
            stock: 'stock',
            name: 'name',
            active: 'active',
            validateQrcode: 'validateQrcode',
          }

          const backendField = fieldMapping[field] || field

          await axiosInstance.put(`/product/${product._id}`, {
            [backendField]: processedValue,
          })

          // Update last saved values
          setLastSavedValues((prev) => ({
            ...prev,
            [field]: value,
          }))

          // Show subtle success feedback
          const fieldLabels: { [key: string]: string } = {
            name: 'Nome',
            newPrice: 'Preço',
            cost: 'Custo',
            stock: 'Estoque',
            active: 'Status',
            validateQrcode: 'QR Code',
          }

          enqueueSnackbar(`${fieldLabels[field] || 'Campo'} salvo`, {
            variant: 'success',
            autoHideDuration: 800,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          })
        } catch (error: any) {
          enqueueSnackbar(`Erro ao salvar ${field}`, {
            variant: 'error',
            autoHideDuration: 1500,
          })
        } finally {
          setSavingFields((prev) => ({ ...prev, [field]: false }))
        }
      }

      // Execute immediately or with debounce
      if (immediate) {
        await performSave()
      } else {
        // Set timeout for debounced save
        saveTimeoutRef.current[field] = setTimeout(performSave, 1000)
      }
    },
    [product._id, lastSavedValues, enqueueSnackbar],
  )

  const handleFieldClick = (field: string) => {
    setEditingField(field)
    setTimeout(() => {
      const input = inputRefs.current[field]
      if (input) {
        input.focus()
        input.select()
      }
    }, 0)
  }

  const handleKeyDown = (event: React.KeyboardEvent, field: string) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setEditingField(null)
    } else if (event.key === 'Escape') {
      setEditingField(null)
    }
  }

  const handleBlur = (field: string, value: any) => {
    setEditingField(null)
    autoSave(field, value, true)
  }

  const handleChange = (field: string, value: any) => {
    autoSave(field, value, false)
  }

  const handleConfirmDelete = async () => {
    if (isDeleting) return

    if (!product._id || product._id === 'search' || product._id.toString().startsWith('temp_')) {
      onRemove()
      confirm.onFalse()
      enqueueSnackbar('Produto removido', {
        variant: 'success',
        autoHideDuration: 1000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
      return
    }

    setIsDeleting(true)
    try {
      await axiosInstance.delete(`/product/${product._id}`)
      onRemove()
      confirm.onFalse()
      enqueueSnackbar('Produto deletado com sucesso', {
        variant: 'success',
        autoHideDuration: 1500,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      })
    } catch (error: any) {
      enqueueSnackbar('Erro ao deletar produto', {
        variant: 'error',
        autoHideDuration: 2000,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      Object.values(saveTimeoutRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout)
      })
    },
    [],
  )

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2,
        borderRadius: 2,
        opacity: isDeleting ? 0.5 : 1,
        transition: 'all 0.3s ease-in-out',
        pointerEvents: isDeleting ? 'none' : 'auto',
        filter: isDeleting ? 'grayscale(0.5)' : 'none',
        '&:hover:not([data-deleting="true"])': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
      data-deleting={isDeleting}
    >
      <Stack spacing={2}>
        {/* Header with name and status */}
        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
          <Stack direction='row' alignItems='center' spacing={1} flex={1}>
            <Iconify icon='mdi:package-variant' sx={{ color: 'primary.main', fontSize: 18 }} />
            <Controller
              name={`categories.${categoryIndex}.products.${productIndex}.name`}
              control={control}
              render={({ field }) =>
                editingField === 'name' ? (
                  <TextField
                    {...field}
                    inputRef={(el) => {
                      inputRefs.current.name = el
                    }}
                    size='small'
                    variant='standard'
                    fullWidth
                    onKeyDown={(e) => handleKeyDown(e, 'name')}
                    onBlur={(e) => handleBlur('name', e.target.value)}
                    onChange={(e) => {
                      field.onChange(e.target.value)
                      handleChange('name', e.target.value)
                    }}
                  />
                ) : (
                  <Typography
                    variant='subtitle2'
                    fontWeight={600}
                    onClick={() => handleFieldClick('name')}
                    sx={{
                      cursor: 'pointer',
                      flex: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        px: 0.5,
                      },
                    }}
                  >
                    {field.value}
                  </Typography>
                )
              }
            />
            {savingFields.name && <CircularProgress size={12} />}
          </Stack>

          <Stack direction='row' alignItems='center' spacing={1}>
            {/* QR Code Switch */}
            <Stack direction='column' alignItems='center' spacing={0.5}>
              <Controller
                name={`categories.${categoryIndex}.products.${productIndex}.validateQrcode`}
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={
                      field.value?.enabled !== undefined
                        ? field.value.enabled
                        : Boolean(field.value)
                    }
                    onChange={(event) => {
                      const newValue = { enabled: event.target.checked }
                      field.onChange(newValue)
                      autoSave('validateQrcode', newValue, true)
                    }}
                    size='small'
                    color='success'
                  />
                )}
              />
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem' }}>
                QR
              </Typography>
              {savingFields.validateQrcode && <CircularProgress size={10} />}
            </Stack>

            {/* Active Switch */}
            <Stack direction='column' alignItems='center' spacing={0.5}>
              <Controller
                name={`categories.${categoryIndex}.products.${productIndex}.active`}
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked)
                      autoSave('active', event.target.checked, true)
                    }}
                    size='small'
                    color='primary'
                  />
                )}
              />
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem' }}>
                Ativo
              </Typography>
              {savingFields.active && <CircularProgress size={10} />}
            </Stack>

            <IconButton
              size='small'
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={isDeleting}
              sx={{ transition: 'all 0.2s' }}
            >
              {isDeleting ? (
                <CircularProgress size={16} />
              ) : (
                <Iconify icon='solar:menu-dots-bold' />
              )}
            </IconButton>

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { minWidth: 180, boxShadow: 3 } } }}
            >
              {allCategories.length > 1 && (
                <MenuItem
                  disabled
                  sx={{ typography: 'caption', color: 'text.disabled', py: 0.5, minHeight: 'auto' }}
                >
                  Mover para...
                </MenuItem>
              )}
              {allCategories
                .map((cat: any, idx: number) => ({ ...cat, _catIdx: idx }))
                .filter((_: any, idx: number) => idx !== categoryIndex)
                .map((cat: any) => (
                  <MenuItem
                    key={cat._id}
                    onClick={() => {
                      setMenuAnchor(null)
                      onMoveToCategory?.(cat._catIdx)
                    }}
                    sx={{ fontSize: '0.85rem' }}
                  >
                    <ListItemIcon>
                      <Iconify icon='solar:folder-send-bold-duotone' width={18} />
                    </ListItemIcon>
                    <ListItemText primary={cat.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              {allCategories.length > 1 && <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 0.5 }} />}
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  confirm.onTrue()
                }}
                sx={{ color: 'error.main', fontSize: '0.85rem' }}
              >
                <ListItemIcon>
                  <Iconify icon='solar:trash-bin-minimalistic-bold' width={18} sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText primary='Deletar' primaryTypographyProps={{ variant: 'body2' }} />
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title='Deletar Produto'
          content={
            <Typography variant='body2'>
              Tem certeza que deseja deletar o produto <strong>{product.name}</strong>? Esta ação não
              pode ser desfeita.
            </Typography>
          }
          action={
            <LoadingButton
              variant='contained'
              color='error'
              onClick={handleConfirmDelete}
              loading={isDeleting}
              loadingIndicator='Deletando...'
            >
              Deletar
            </LoadingButton>
          }
        />

        {/* Price fields in grid */}
        <Stack spacing={2}>
          <Stack direction='row' spacing={2}>
            {/* Original Price */}
            <Box flex={1}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 0.5 }}
              >
                Preço Original
              </Typography>
              <Controller
                name={`categories.${categoryIndex}.products.${productIndex}.price`}
                control={control}
                render={({ field }) => (
                  <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.875rem' }}>
                    R$ {field.value || '0,00'}
                  </Typography>
                )}
              />
            </Box>

            {/* New Price */}
            <Box flex={1}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 0.5 }}
              >
                Novo Preço
              </Typography>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Controller
                  name={`categories.${categoryIndex}.products.${productIndex}.newPrice`}
                  control={control}
                  render={({ field }) =>
                    editingField === 'newPrice' ? (
                      <TextField
                        {...field}
                        inputRef={(el) => {
                          inputRefs.current.newPrice = el
                        }}
                        size='small'
                        variant='standard'
                        fullWidth
                        onKeyDown={(e) => handleKeyDown(e, 'newPrice')}
                        onBlur={(e) => handleBlur('newPrice', e.target.value)}
                        onChange={(e) => {
                          const masked = masks.currency(e.target.value)
                          field.onChange(masked)
                          handleChange('newPrice', masked)
                        }}
                      />
                    ) : (
                      <Typography
                        variant='body2'
                        onClick={() => handleFieldClick('newPrice')}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            px: 0.5,
                          },
                        }}
                      >
                        R$ {field.value || '0,00'}
                      </Typography>
                    )
                  }
                />
                {savingFields.newPrice && <CircularProgress size={12} />}
              </Stack>
            </Box>
          </Stack>

          <Stack direction='row' spacing={2}>
            {/* Cost */}
            <Box flex={1}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 0.5 }}
              >
                Custo
              </Typography>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Controller
                  name={`categories.${categoryIndex}.products.${productIndex}.cost`}
                  control={control}
                  render={({ field }) =>
                    editingField === 'cost' ? (
                      <TextField
                        {...field}
                        inputRef={(el) => {
                          inputRefs.current.cost = el
                        }}
                        size='small'
                        variant='standard'
                        fullWidth
                        onKeyDown={(e) => handleKeyDown(e, 'cost')}
                        onBlur={(e) => handleBlur('cost', e.target.value)}
                        onChange={(e) => {
                          const masked = masks.currency(e.target.value)
                          field.onChange(masked)
                          handleChange('cost', masked)
                        }}
                      />
                    ) : (
                      <Typography
                        variant='body2'
                        onClick={() => handleFieldClick('cost')}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            px: 0.5,
                          },
                        }}
                      >
                        R$ {field.value || '0,00'}
                      </Typography>
                    )
                  }
                />
                {savingFields.cost && <CircularProgress size={12} />}
              </Stack>
            </Box>

            {/* Stock */}
            <Box flex={1}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mb: 0.5 }}
              >
                Estoque
              </Typography>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Controller
                  name={`categories.${categoryIndex}.products.${productIndex}.stock`}
                  control={control}
                  render={({ field }) =>
                    editingField === 'stock' ? (
                      <TextField
                        {...field}
                        inputRef={(el) => {
                          inputRefs.current.stock = el
                        }}
                        size='small'
                        variant='standard'
                        type='number'
                        fullWidth
                        onKeyDown={(e) => handleKeyDown(e, 'stock')}
                        onBlur={(e) => handleBlur('stock', parseInt(e.target.value, 10) || 0)}
                        onChange={(e) => {
                          field.onChange(e.target.value)
                          handleChange('stock', parseInt(e.target.value, 10) || 0)
                        }}
                      />
                    ) : (
                      <Typography
                        variant='body2'
                        onClick={() => handleFieldClick('stock')}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            px: 0.5,
                          },
                        }}
                      >
                        {field.value || 0}
                      </Typography>
                    )
                  }
                />
                {savingFields.stock && <CircularProgress size={12} />}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
})
